import { useState, useEffect, useRef, useCallback } from 'react'
import {
  MessageCircle,
  ThumbsUp,
  ThumbsDown,
  Send,
  Loader2,
  HelpCircle,
  Settings as SettingsIcon,
  Lock,
  Bug,
  Info,
  RotateCcw,
  Ticket,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  sendSupportMessage,
  submitSupportFeedback,
  endSupportSession,
  createSupportEscalation,
} from '@/lib/api'
import type { SupportMessage, SupportChatResponse } from '@/types/support'
import ReactMarkdown from 'react-markdown'
import { useAdminToolbar } from '@/contexts/AdminToolbarContext'
import { useAuth } from '@/contexts/AuthContext'
import { LinaTypingIndicator } from '@/components/shared/loaders'
import XButton from '@/components/shared/custom-buttons/X'
import {
  clearSupportChatState,
  loadSupportChatState,
  saveSupportChatState,
} from '@/lib/support-chat-storage'

// Support categories matching our knowledge base
const SUPPORT_CATEGORIES = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    icon: Info,
    description: 'Login, setup, and first steps',
  },
  {
    id: 'feature',
    label: 'Using Features',
    icon: HelpCircle,
    description: "How to use LINA's tools",
  },
  {
    id: 'account',
    label: 'Account Settings',
    icon: SettingsIcon,
    description: 'Profile, notifications, preferences',
  },
  {
    id: 'privacy',
    label: 'Privacy & Security',
    icon: Lock,
    description: 'Data safety and protection',
  },
  {
    id: 'troubleshooting',
    label: 'Troubleshooting',
    icon: Bug,
    description: 'Errors and technical issues',
  },
] as const

type SupportCategory = (typeof SUPPORT_CATEGORIES)[number]['id'] | null

function isTicketConfirmationYes(text: string): boolean {
  return /^(yes|y|yeah|yep|sure|ok|okay|confirm|please|go ahead|create(\s+a?\s*(support\s*)?ticket)?)/i.test(
    text.trim(),
  )
}

function isTicketConfirmationNo(text: string): boolean {
  return /^(no|n|nope|cancel|don't|dont|not now|keep chatting)/i.test(text.trim())
}

interface SupportChatProps {
  /** Initial page context */
  currentPage?: string
  /** Initial feature context */
  feature?: string
  /** Optional error state to include */
  errorState?: {
    error_code?: string
    error_message?: string
    timestamp?: string
  }
}

export function SupportChat({ currentPage, feature, errorState }: SupportChatProps) {
  const { user } = useAuth()
  const { showSupportChat } = useAdminToolbar()
  const userId = user?.id ?? null

  const [isOpen, setIsOpen] = useState(false)
  const [hydrated, setHydrated] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [messages, setMessages] = useState<SupportMessage[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [lastResponse, setLastResponse] = useState<SupportChatResponse | null>(null)
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false)
  const [ticketSubmitted, setTicketSubmitted] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<SupportCategory>(null)
  const [showCategorySelection, setShowCategorySelection] = useState(true)
  const [awaitingTicketConfirmation, setAwaitingTicketConfirmation] = useState(false)
  const [ticketConfirmationTimeout, setTicketConfirmationTimeout] = useState<NodeJS.Timeout | null>(
    null,
  )
  const [menuOpen, setMenuOpen] = useState(false)
  const [confirmingRestart, setConfirmingRestart] = useState(false)
  const [forceTicketPrompt, setForceTicketPrompt] = useState(false)
  const [ticketIssue, setTicketIssue] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const showTicketPanel =
    (forceTicketPrompt || Boolean(lastResponse?.escalation?.suggested)) &&
    !ticketSubmitted &&
    !awaitingTicketConfirmation

  const buildIssueSummary = useCallback(() => {
    const trimmed = ticketIssue.trim()
    if (trimmed) return trimmed

    return messages
      .filter((m) => m.role === 'user')
      .map((m) => m.content)
      .slice(-3)
      .join(' | ')
  }, [ticketIssue, messages])

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, isLoading, scrollToBottom])

  // Restore conversation from local storage (24h max)
  useEffect(() => {
    if (!userId) {
      setHydrated(true)
      return
    }

    const saved = loadSupportChatState(userId)
    if (saved) {
      setSessionId(saved.sessionId)
      setMessages(saved.messages)
      setSelectedCategory(saved.selectedCategory as SupportCategory)
      setShowCategorySelection(saved.showCategorySelection)
      setTicketSubmitted(saved.ticketSubmitted)
      setAwaitingTicketConfirmation(saved.awaitingTicketConfirmation)
      setLastResponse(saved.lastResponse)
    }

    setHydrated(true)
  }, [userId])

  // Persist conversation locally while signed in
  useEffect(() => {
    if (!userId || !hydrated) return

    saveSupportChatState(userId, {
      sessionId,
      messages,
      selectedCategory,
      showCategorySelection,
      ticketSubmitted,
      awaitingTicketConfirmation,
      lastResponse,
    })
  }, [
    userId,
    hydrated,
    sessionId,
    messages,
    selectedCategory,
    showCategorySelection,
    ticketSubmitted,
    awaitingTicketConfirmation,
    lastResponse,
  ])

  const showGreeting = useCallback(() => {
    setMessages([
      {
        id: `greeting-${Date.now()}`,
        session_id: '',
        role: 'assistant',
        content: "👋 Hi! I'm LINA, your support assistant.\n\nHow can I help you today?",
        created_at: new Date().toISOString(),
      },
    ])
  }, [])

  // Show initial greeting when chat opens with no saved messages
  useEffect(() => {
    if (hydrated && isOpen && messages.length === 0 && showCategorySelection) {
      showGreeting()
    }
  }, [hydrated, isOpen, messages.length, showCategorySelection, showGreeting])

  const handleCategorySelect = (category: SupportCategory) => {
    setSelectedCategory(category)
    setShowCategorySelection(false)

    // Add category selection to chat
    const categoryName = SUPPORT_CATEGORIES.find((c) => c.id === category)?.label || 'Other'
    setMessages((prev) => [
      ...prev,
      {
        id: `user-category-${Date.now()}`,
        session_id: sessionId || '',
        role: 'user',
        content: `I need help with: ${categoryName}`,
        created_at: new Date().toISOString(),
      },
      {
        id: `assistant-followup-${Date.now()}`,
        session_id: sessionId || '',
        role: 'assistant',
        content: 'Great! Can you tell me more about what you need help with?',
        created_at: new Date().toISOString(),
      },
    ])
  }

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return

    const userMessage = input.trim()
    setInput('')
    setIsLoading(true)

    // User agreed to create a ticket after bot suggested escalation
    if (awaitingTicketConfirmation) {
      const isYes = isTicketConfirmationYes(userMessage)
      const isNo = isTicketConfirmationNo(userMessage)

      if (isYes || isNo) {
        if (ticketConfirmationTimeout) {
          clearTimeout(ticketConfirmationTimeout)
          setTicketConfirmationTimeout(null)
        }
        setAwaitingTicketConfirmation(false)

        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            session_id: sessionId || '',
            role: 'user',
            content: userMessage,
            created_at: new Date().toISOString(),
          },
        ])

        if (isYes) {
          try {
            await createSupportTicket(lastResponse?.escalation?.reason)
          } finally {
            setIsLoading(false)
          }
          return
        }

        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            session_id: sessionId || '',
            role: 'assistant',
            content:
              "No problem! I won't create a support ticket. Is there anything else I can help you with?",
            created_at: new Date().toISOString(),
          },
        ])
        setIsLoading(false)
        return
      }
    }

    // Hide badge selection after first user message
    if (messages.filter((m) => m.role === 'user').length === 0) {
      setShowCategorySelection(false)
    }

    // Add user message to UI immediately
    const tempUserMessage: SupportMessage = {
      id: `temp-${Date.now()}`,
      session_id: sessionId || '',
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, tempUserMessage])

    try {
      const response = await sendSupportMessage({
        session_id: sessionId,
        message: userMessage,
        context: {
          current_page: currentPage || window.location.pathname,
          category: selectedCategory || undefined,
          feature: feature,
          error_state: errorState,
        },
      })

      if (response.success) {
        setSessionId(response.session_id)
        setLastResponse(response)

        // Replace temp message with real one, add assistant response
        setMessages((prev) => {
          const withoutTemp = prev.filter((m) => !m.id.startsWith('temp-'))
          return [
            ...withoutTemp,
            {
              id: crypto.randomUUID(),
              session_id: response.session_id,
              role: 'user',
              content: userMessage,
              created_at: new Date().toISOString(),
            },
            {
              id: response.message.id || crypto.randomUUID(),
              session_id: response.session_id,
              role: 'assistant',
              content: response.message.text,
              metadata: {
                intent: response.message.intent,
                confidence: response.message.confidence,
              },
              created_at: new Date().toISOString(),
            },
          ]
        })

        // User explicitly asked for human help — create ticket (their message is consent)
        if (response.escalation?.user_requested && !ticketSubmitted) {
          await createSupportTicket(response.escalation.reason)
        } else if (response.escalation?.suggested && !ticketSubmitted) {
          setTicketIssue((prev) => prev || userMessage)
          setShowCategorySelection(false)
        }
      }
    } catch (error) {
      console.error('Support chat error:', error)
      // Add error message
      setMessages((prev) => [
        ...prev.filter((m) => !m.id.startsWith('temp-')),
        {
          id: crypto.randomUUID(),
          session_id: sessionId || '',
          role: 'assistant',
          content:
            'Sorry, I encountered an error. Please try again or contact info@uniquelynd.com.',
          created_at: new Date().toISOString(),
        },
      ])
    } finally {
      setIsLoading(false)
    }
  }

  const handleFeedback = async (frontendMessageId: string, rating: -1 | 1) => {
    try {
      const message = messages.find((m) => m.id === frontendMessageId)
      if (!message || !sessionId) {
        console.error('Cannot submit feedback: message or session not found')
        return
      }

      await submitSupportFeedback(message.id, rating)

      // Update message to show feedback was submitted
      setMessages((prev) =>
        prev.map((m) =>
          m.id === frontendMessageId
            ? { ...m, metadata: { ...m.metadata, feedback_submitted: true } }
            : m,
        ),
      )
    } catch (error) {
      console.error('Feedback submission error:', error)
    }
  }

  const clearTicketConfirmationTimeout = useCallback(() => {
    if (ticketConfirmationTimeout) {
      clearTimeout(ticketConfirmationTimeout)
      setTicketConfirmationTimeout(null)
    }
  }, [ticketConfirmationTimeout])

  const resetChatInterface = useCallback(
    (options?: { clearStorage?: boolean }) => {
      clearTicketConfirmationTimeout()
      if (options?.clearStorage && userId) {
        clearSupportChatState(userId)
      }

      setSessionId(null)
      setMessages([])
      setInput('')
      setLastResponse(null)
      setTicketSubmitted(false)
      setAwaitingTicketConfirmation(false)
      setSelectedCategory(null)
      setShowCategorySelection(true)
      setForceTicketPrompt(false)
      setConfirmingRestart(false)
      setMenuOpen(false)
    },
    [clearTicketConfirmationTimeout, userId],
  )

  const handleRestartConversation = async () => {
    if (sessionId) {
      try {
        await endSupportSession(sessionId)
      } catch (error) {
        console.error('Error ending session on restart:', error)
      }
    }

    resetChatInterface({ clearStorage: true })
  }

  const handleRequestTicketFromMenu = () => {
    setMenuOpen(false)
    setConfirmingRestart(false)
    setForceTicketPrompt(true)
    setShowCategorySelection(false)

    const recentUserText = messages
      .filter((m) => m.role === 'user')
      .map((m) => m.content)
      .slice(-2)
      .join('\n')

    if (recentUserText) {
      setTicketIssue(recentUserText)
    }
  }

  const handleClose = () => {
    clearTicketConfirmationTimeout()
    setMenuOpen(false)
    setConfirmingRestart(false)
    setIsOpen(false)
  }

  const createSupportTicket = async (escalationReason?: string) => {
    if (ticketSubmitted || isSubmittingTicket) return

    const issueSummary = buildIssueSummary()
    if (!issueSummary.trim()) return

    setIsSubmittingTicket(true)

    try {
      let activeSessionId = sessionId

      if (!activeSessionId) {
        const bootstrap = await sendSupportMessage({
          session_id: null,
          message: issueSummary,
          context: {
            current_page: currentPage || window.location.pathname,
            category: selectedCategory || undefined,
            feature,
            error_state: errorState,
          },
        })

        if (!bootstrap.success) {
          throw new Error('Could not start support session')
        }

        activeSessionId = bootstrap.session_id
        setSessionId(activeSessionId)
      }

      const pageContext = window.location.pathname + window.location.search

      await createSupportEscalation({
        session_id: activeSessionId,
        issue_summary: issueSummary,
        priority: 'medium',
        additional_context:
          escalationReason ||
          lastResponse?.escalation?.reason ||
          (forceTicketPrompt ? 'User requested support ticket from menu' : undefined),
        context: {
          current_page: pageContext || window.location.pathname,
          browser_info: navigator.userAgent,
          last_action: issueSummary.split('\n').pop(),
          feature: selectedCategory
            ? SUPPORT_CATEGORIES.find((c) => c.id === selectedCategory)?.label
            : feature,
        },
      })

      setTicketSubmitted(true)
      setForceTicketPrompt(false)
      setTicketIssue('')
      setAwaitingTicketConfirmation(false)
      if (ticketConfirmationTimeout) {
        clearTimeout(ticketConfirmationTimeout)
        setTicketConfirmationTimeout(null)
      }

      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          session_id: activeSessionId,
          role: 'assistant',
          content:
            '✅ **Support ticket created!**\n\nOur team has been notified and will respond via email soon. You can continue chatting here or close this window.',
          created_at: new Date().toISOString(),
        },
      ])
    } catch (error) {
      console.error('Ticket submission error:', error)
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          session_id: sessionId || '',
          role: 'assistant',
          content:
            'Sorry, I could not create a support ticket. Please try again or email info@uniquelynd.com directly.',
          created_at: new Date().toISOString(),
        },
      ])
    } finally {
      setIsSubmittingTicket(false)
    }
  }

  const handleDismissTicketPanel = () => {
    setForceTicketPrompt(false)
    setTicketIssue('')
    setLastResponse((prev) =>
      prev
        ? {
            ...prev,
            escalation: prev.escalation ? { ...prev.escalation, suggested: false } : undefined,
          }
        : prev,
    )
    if (messages.filter((m) => m.role === 'user').length === 0) {
      setShowCategorySelection(true)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Signed-in users see support chat; admins can force-enable via toolbar for QA
  if (!user && !showSupportChat) {
    return null
  }

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-20 h-10 w-10 rounded-full shadow-lg z-40"
        size="icon"
        title="Ask Lina Support"
      >
        <MessageCircle className="h-6 w-6" />
      </Button>
    )
  }

  return (
    <Card className="fixed bottom-4 right-4 w-96 h-[min(600px,calc(100vh-80px))] overflow-hidden bg-background/90 shadow-2xl flex flex-col z-50">
      <CardHeader className="flex flex-row items-center justify-between px-4 py-3 shrink-0">
        <CardTitle className="text-fluid-base font-semibold">LINA Support</CardTitle>
        <div className="flex items-center gap-0.5">
          <Popover
            open={menuOpen}
            onOpenChange={(open) => {
              setMenuOpen(open)
              if (!open) setConfirmingRestart(false)
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                aria-label="Support chat options"
              >
                <SettingsIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-56 p-2 -mt-10 -mr-10 bg-background/80 glass-subtle"
            >
              {confirmingRestart ? (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground leading-relaxed px-1">
                    Start a new conversation? Your local chat history will be cleared.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 h-8 text-xs"
                      onClick={() => setConfirmingRestart(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      className="flex-1 h-8 text-xs"
                      onClick={() => void handleRestartConversation()}
                    >
                      Confirm
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-[10px] px-2 py-2 text-fluid-xs text-left hover:bg-primary/10 transition-colors"
                    onClick={() => setConfirmingRestart(true)}
                  >
                    <RotateCcw className="h-4 w-4 text-muted-foreground" />
                    Restart conversation
                  </button>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-[10px] px-2 py-2 text-fluid-xs text-left hover:bg-primary/10 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                    disabled={ticketSubmitted}
                    onClick={handleRequestTicketFromMenu}
                  >
                    <Ticket className="h-4 w-4 text-muted-foreground" />
                    Request support ticket
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          <XButton onClick={handleClose} />
        </div>
      </CardHeader>

      <CardContent className="flex-1 min-h-0 overflow-hidden p-0">
        <ScrollArea className="h-full px-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center p-4">
              <MessageCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold mb-2">How can I help?</h3>
              <p className="text-fluid-xs text-muted-foreground">
                Ask me about using LINA's features, troubleshooting issues, or account questions.
              </p>
            </div>
          )}

          <div className="space-y-4 pb-4">
            {messages.map((message) => {
              const isSupport = message.role === 'assistant'
              const senderName = isSupport ? 'LINA' : 'You'
              const avatarInitial = isSupport ? 'L' : 'Y'
              const avatarGradient = isSupport
                ? 'var(--gradient-accent)'
                : 'var(--gradient-secondary)'
              const bubbleStyle = isSupport
                ? {
                    backgroundColor: 'hsl(var(--muted-secondary))',
                    color: 'hsl(var(--text-primary))',
                  }
                : {
                    backgroundColor: 'hsl(var(--muted-primary))',
                    color: 'hsl(var(--text-primary))',
                  }

              return (
                <div
                  key={message.id}
                  className={`flex ${isSupport ? 'justify-start' : 'justify-end'} gap-3`}
                >
                  {/* Avatar for Support message - bottom aligned */}
                  {isSupport && (
                    <div className="flex flex-col items-center justify-end">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-fluid-xs font-bold shrink-0 shadow-md"
                        style={{ background: avatarGradient }}
                        title="LINA Support"
                      >
                        {avatarInitial}
                      </div>
                    </div>
                  )}

                  {/* Message content with sender name */}
                  <div className="flex flex-col gap-1 max-w-[75%] min-w-0">
                    {/* Sender name */}
                    <span
                      className={`text-fluid-xs font-semibold text-muted-foreground px-1 ${isSupport ? 'text-left' : 'text-right'}`}
                    >
                      {senderName}
                    </span>

                    {/* Message bubble */}
                    <div className="flex items-start gap-2 min-w-0">
                      <div className="flex-1 p-3 rounded-lg shadow-md" style={bubbleStyle}>
                        {message.role === 'assistant' ? (
                          <div className="prose max-w-none dark:prose-invert text-fluid-xs [&>*]:text-fluid-xs">
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                          </div>
                        ) : (
                          <p className="text-fluid-xs leading-relaxed whitespace-pre-wrap break-words">
                            {message.content}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Feedback buttons */}
                    {message.role === 'assistant' &&
                      message.metadata &&
                      !message.metadata.feedback_submitted && (
                        <div className="flex items-center gap-1 pt-1 -pb-1 -mb-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 btn-green-glow text-muted-foreground hover:text-green-600 dark:hover:text-green-400"
                            onClick={() => handleFeedback(message.id, 1)}
                            aria-label="Helpful"
                          >
                            <ThumbsUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 btn-red-glow text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                            onClick={() => handleFeedback(message.id, -1)}
                            aria-label="Not helpful"
                          >
                            <ThumbsDown className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                  </div>

                  {/* Avatar for user message - bottom aligned */}
                  {!isSupport && (
                    <div className="flex flex-col items-center justify-end">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-fluid-xs font-bold shrink-0 shadow-md"
                        style={{ background: avatarGradient }}
                        title="You"
                      >
                        {avatarInitial}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}

            {/* Category Selection Badges */}
            {showCategorySelection && !showTicketPanel && messages.length > 0 && (
              <div className="flex flex-col gap-2 mt-2 px-2">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Select a category:</p>
                <div className="flex flex-wrap gap-2">
                  {SUPPORT_CATEGORIES.map((category) => {
                    const Icon = category.icon
                    return (
                      <Badge
                        key={category.id}
                        variant="outline"
                        className="glass-subtle cursor-pointer hover:bg-primary/20 hover:font-semibold hover:text-primary transition-colors px-3 py-1.5 text-fluid-2xs"
                        onClick={() => handleCategorySelect(category.id)}
                      >
                        <Icon className="h-3 w-3 mr-1.5" />
                        {category.label}
                      </Badge>
                    )
                  })}
                  <Badge
                    variant="outline"
                    className="glass-subtle cursor-pointer hover:bg-primary/20 hover:font-semibold hover:text-primary transition-colors px-3 py-1.5 text-fluid-2xs"
                    onClick={() => handleCategorySelect(null)}
                  >
                    <HelpCircle className="h-3 w-3 mr-1.5" />
                    Other
                  </Badge>
                </div>
              </div>
            )}

            {isLoading && <LinaTypingIndicator message="Support is typing" />}
            <div ref={messagesEndRef} className="h-px shrink-0" aria-hidden />
          </div>
        </ScrollArea>
      </CardContent>

      <CardFooter className="flex flex-col gap-2 p-4 shrink-0">
        {showTicketPanel && (
          <div className="glass w-full rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2 min-w-0">
                <Ticket className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">
                    {forceTicketPrompt ? 'Request support' : 'Need more help?'}
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-muted-foreground">
                      {forceTicketPrompt
                        ? 'Describe your issue and our team will follow up by email.'
                        : lastResponse?.escalation?.reason ||
                          'Share a few details so we can help you faster.'}
                    </p>
                  </div>
                </div>
              </div>
              {(forceTicketPrompt || lastResponse?.escalation?.suggested) && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-7 px-2 text-xs shrink-0 font-semibold"
                  onClick={handleDismissTicketPanel}
                  disabled={isSubmittingTicket}
                >
                  {forceTicketPrompt ? 'Cancel' : 'Keep chatting'}
                </Button>
              )}
            </div>

            <div className="space-y-1.5">
              <p className="text-fluid-xs font-semibold text-foreground">Category (optional)</p>
              <div className="flex flex-wrap gap-1.5">
                {SUPPORT_CATEGORIES.map((category) => {
                  const Icon = category.icon
                  const isSelected = selectedCategory === category.id
                  return (
                    <Badge
                      key={category.id}
                      variant={isSelected ? 'default' : 'outline'}
                      className="cursor-pointer px-3 py-1 text-fluid-xs glass-subtle hover:bg-primary/10 hover:font-semibold hover:text-primary transition-colors"
                      onClick={() => setSelectedCategory(isSelected ? null : category.id)}
                    >
                      <Icon className="h-3.5 w-3.5 mr-1" />
                      {category.label}
                    </Badge>
                  )
                })}
              </div>
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="support-ticket-issue"
                className="text-fluid-xs font-semibold text-foreground"
              >
                What do you need help with?
              </label>
              <Textarea
                id="support-ticket-issue"
                value={ticketIssue}
                onChange={(e) => setTicketIssue(e.target.value)}
                placeholder="Describe the issue, what you tried, and any error messages..."
                className="min-h-[100px] resize-none text-fluid-xs glass-subtle border-none focus-visible:ring-2 focus-visible:ring-offset-0 focus-visible:ring-primary/30"
                disabled={isSubmittingTicket}
              />
            </div>

            <Button
              onClick={() => createSupportTicket()}
              disabled={isSubmittingTicket || !buildIssueSummary().trim()}
              className="w-full"
              variant="primary"
            >
              {isSubmittingTicket ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting ticket...
                </>
              ) : (
                'Submit support ticket'
              )}
            </Button>

            {lastResponse?.follow_ups &&
              lastResponse.follow_ups.length > 0 &&
              !forceTicketPrompt && (
                <div className="pt-2 border-t border-border space-y-1">
                  <p className="text-xs text-muted-foreground font-medium">Or try asking:</p>
                  {lastResponse.follow_ups.map((followUp, i) => (
                    <button
                      key={i}
                      type="button"
                      className="text-xs text-primary hover:underline block text-left w-full"
                      onClick={() => {
                        handleDismissTicketPanel()
                        setInput(followUp)
                      }}
                    >
                      • {followUp}
                    </button>
                  ))}
                </div>
              )}
          </div>
        )}

        {!showTicketPanel && (
          <>
            <div className="flex w-full gap-2 items-center">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask a question..."
                className="min-h-[80px] max-h-[150px] text-fluid-xs resize-none glass-subtle border-none focus-visible:ring-2 focus-visible:ring-offset-0 focus-visible:ring-primary/30"
                disabled={isLoading}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!input.trim() || isLoading}
                variant="primary"
                size="icon"
                className="rounded-full p-4"
                aria-label="Send message"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-background" />
                ) : (
                  <Send className="h-4 w-4 text-background" />
                )}
              </Button>
            </div>

            <p className="text-fluid-2xs font-medium text-muted-foreground">
              Ask about features, troubleshooting, or account help
            </p>
          </>
        )}
      </CardFooter>
    </Card>
  )
}
